import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { Form, Formik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { useContextSelector } from 'use-context-selector';
import Button from '@components/atoms/button/button';
import { SellerFormValueType as FormValueType } from '@interfaces/models/seller-info/seller-information';
import SellerInformationError from '@components/seller-information/seller-information-error/seller-information-error';
import SellerInformationSuccess from '@components/seller-information/seller-information-success/seller-information-success';
import useUser from '@hooks/user/use-user';
import Spinner from '@components/atoms/spinner/spinner';
import useWindowSize from '@hooks/use-window-size';
import sanitizer from '@helpers/sanitizer';
import useSellerTaxInformation from '@hooks/seller-information/use-seller-information-query';
import { SellerInformationContext } from '@context/seller-information.context';
import { SellerInformationKeys } from '@enums/react-query-keys';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useAnalytics } from '@context/analytics/analytics.context';
import SellerInformationErrorModal from '../seller-information-error-modal/seller-information-error-modal';
import SellerInformationContent from './seller-information-content';
import styles from './seller-information-form.module.scss';

const SellerInformationForm: React.FC = () => {
  const { t } = useTranslation('get-paid');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();
  const { sendAnalyticEvent: sendSellerInfoAnalyticEvents } = useAnalyticEvents('seller_info');
  const { user, isAuthenticated } = useUser();
  const { isWiderThanMd } = useWindowSize();
  const { isLoading, taxInformation } = useSellerTaxInformation();
  const { updateDataLayer, scriptsEventData } = useAnalytics();

  const [isEdited, setIsEdited] = useState<boolean>(false);

  const formInitValue = useContextSelector(SellerInformationContext, (s) => s.formInitValue);
  const saveSellerInformation = useContextSelector(SellerInformationContext, (s) => s.saveSellerInformation);
  const userFlags = useContextSelector(SellerInformationContext, (s) => s.userFlags);
  const initialErrorObject = useContextSelector(SellerInformationContext, (s) => s.initialErrorObject);
  const validator = useContextSelector(SellerInformationContext, (s) => s.validator);
  const showSuccess = useContextSelector(SellerInformationContext, (s) => s.showSuccess);
  const payload = useContextSelector(SellerInformationContext, (s) => s.payload);
  const dateAndAgeErrors = useContextSelector(SellerInformationContext, (s) => s.dateAndAgeErrors);
  const selectedCountry = useContextSelector(SellerInformationContext, (s) => s.selectedCountry);
  const submitCount = useContextSelector(SellerInformationContext, (s) => s.submitCount);
  const error = useContextSelector(SellerInformationContext, (s) => s.error);
  const setError = useContextSelector(SellerInformationContext, (s) => s.setError);
  const setShowSuccess = useContextSelector(SellerInformationContext, (s) => s.setShowSuccess);
  const openSellerInfoErrorModal = useContextSelector(SellerInformationContext, (s) => s.openSellerInfoErrorModal);
  const setOpenSellerInfoErrorModal = useContextSelector(
    SellerInformationContext,
    (s) => s.setOpenSellerInfoErrorModal,
  );

  useEffect(() => {
    if (isAuthenticated) {
      updateDataLayer({
        env_template: 'seller_info',
        pageUrl: '/seller-information',
        screen_category: 'seller_info',
        screen_subcategory: 'tax_form',
        screen_name: '/tax_form',
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && scriptsEventData?.isTcEvents1Loaded && scriptsEventData?.isTcEvents7Loaded) {
      if ((!isEdited && taxInformation?.tin) || showSuccess) {
        sendSellerInfoAnalyticEvents('seller_tax_page', {
          label: 'success_page',
        });
      }
    }
  }, [
    showSuccess,
    isEdited,
    taxInformation?.tin,
    scriptsEventData?.isTcEvents1Loaded,
    scriptsEventData?.isTcEvents7Loaded,
    isAuthenticated,
  ]);

  useEffect(() => {
    setError(submitCount > 3);
  }, [submitCount]);

  useEffect(() => {
    if (openSellerInfoErrorModal && submitCount <= 3) {
      sendSellerInfoAnalyticEvents('seller_tax_error');
    }
  }, [openSellerInfoErrorModal]);

  const submitForm = async (values: FormValueType, { setSubmitting }) => {
    sendSellerInfoAnalyticEvents('seller_tax_submit');
    await saveSellerInformation(values);
    setSubmitting(false);
  };

  const taxFormDescription =
    selectedCountry?.code === 'US'
      ? t(
          'GET_PAID.TAX_INFORMATION_FORM.DESCRIPTION.US',
          'To comply with US tax regulations, please provide your tax information below',
        )
      : t(
          'GET_PAID.TAX_INFORMATION_FORM.DESCRIPTION.EU',
          'To comply with EU tax regulations, please provide your tax information below before January 1.',
        );

  const handleEditInformation = async () => {
    sendSellerInfoAnalyticEvents('seller_tax_edit');
    await queryClient.prefetchQuery([SellerInformationKeys.GET_TAX_INFORMATION, user?.id]);
    setShowSuccess(false);
    setIsEdited(true);
  };

  const flagAndDateErrors =
    userFlags?.isUSC2CUser || userFlags?.isEUC2CUser ? dateAndAgeErrors.dateError || dateAndAgeErrors.ageError : false;

  const handleErrorDialogClose = (): void => {
    sendSellerInfoAnalyticEvents('seller_tax_error_close');
    setOpenSellerInfoErrorModal(false);
  };

  return (
    <>
      {isLoading ? (
        <div className={styles.sellerInformationLoading}>
          <Spinner />
          <p>{t('GET_PAID.TAX_INFORMATION_FORM.LOADING', `We're retrieving your details to make things easier.`)}</p>
        </div>
      ) : error ? (
        <SellerInformationError />
      ) : showSuccess || (!isEdited && taxInformation?.tin) ? (
        <SellerInformationSuccess
          payload={payload}
          taxInformation={taxInformation}
          onEditInformation={handleEditInformation}
          userFlags={userFlags}
          showSuccess={showSuccess}
          isEdited={isEdited}
        />
      ) : (
        <>
          <div className={styles.sellerInformationTitle}>
            <h2 className={styles.sellerInformationTitle__title}>
              {t('GET_PAID.TAX_INFORMATION_FORM.TITLE', 'Your Tax Information')}
            </h2>
            <p
              className={styles.sellerInformationTitle__subtitle}
              dangerouslySetInnerHTML={{
                __html: sanitizer(taxFormDescription),
              }}
            />
          </div>
          <Formik
            initialValues={formInitValue}
            validate={validator}
            onSubmit={submitForm}
            validateOnBlur={true}
            validateOnMount
            enableReinitialize
            initialTouched={initialErrorObject}
          >
            {({ values, errors, touched, isValid, isSubmitting, handleSubmit, dirty }) => (
              <Form
                className={clsx(styles.sellerInformationForm, { hasLoader: isSubmitting })}
                onSubmit={handleSubmit}
                acceptCharset="utf-8"
                method="POST"
                target="validation"
              >
                <SellerInformationContent
                  values={values}
                  errors={errors}
                  touched={touched}
                  dirty={dirty}
                />
                {/* Submit CTA */}
                <div className={styles.sellerInformationForm__submitBtn}>
                  <Button
                    size="large"
                    variant="primary"
                    fullWidth
                    disabled={!isValid || flagAndDateErrors}
                    type="submit"
                  >
                    {t('GET_PAID.TAX_INFORMATION_FORM.SUBMIT_CTA', 'Submit information')}
                  </Button>
                </div>
                {/* Privacy Policy */}
                <div className={styles.sellerInformationForm__privacyPolicy}>
                  <div
                    className={clsx(
                      styles.sellerInformationForm__privacyPolicy__text,
                      isWiderThanMd ? 'vc-text-H14' : 'vc-text-H12',
                    )}
                  >
                    <span>{t('GET_PAID.PRIVACY_POLICY.LEARN_MORE') + ' '}</span>
                    <a
                      className={styles.sellerInformationForm__privacyPolicy__link}
                      href={tCommon('LEGAL.LINK.PRIVACY_POLICY')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('GET_PAID.PRIVACY_POLICY.PRIVACY_POLICY')}
                    </a>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
          <SellerInformationErrorModal
            isOpen={openSellerInfoErrorModal}
            onClose={handleErrorDialogClose}
          />
        </>
      )}
    </>
  );
};

export default SellerInformationForm;
